import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import AuthLayout, { Field, inputStyle } from './AuthLayout';

const schema = z.object({
  password: z.string().min(8, 'At least 8 characters'),
  passwordConfirm: z.string(),
}).refine(d => d.password === d.passwordConfirm, {
  path: ['passwordConfirm'],
  message: 'Passwords don\'t match',
});
type FormVals = z.infer<typeof schema>;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormVals>({
    resolver: zodResolver(schema),
  });

  async function onSubmit(values: FormVals) {
    setSubmitErr(null);
    const res = await fetch('/api/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: values.password }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      setSubmitErr(body.error === 'invalid_or_expired'
        ? 'This reset link is invalid or has expired. Request a new one.'
        : body.message || 'Could not reset. Try again.');
      return;
    }
    navigate('/login?reset=ok');
  }

  if (!token) {
    return (
      <AuthLayout title="Reset link missing" subtitle="No reset token in the URL. Request a new link.">
        <Link to="/forgot-password" className="btn accent">Request new link</Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose a new password"
      footer={<Link to="/login" style={{ color: 'var(--pri)' }}>Back to sign in</Link>}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Field label="New password (8+ chars)" error={errors.password?.message}>
          <input type="password" autoFocus autoComplete="new-password" {...register('password')} style={inputStyle} />
        </Field>
        <Field label="Confirm password" error={errors.passwordConfirm?.message}>
          <input type="password" autoComplete="new-password" {...register('passwordConfirm')} style={inputStyle} />
        </Field>
        {submitErr && (
          <div style={{ padding: 10, background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13, marginBottom: 14, color: '#991b1b' }}>{submitErr}</div>
        )}
        <button type="submit" className="btn accent" style={{ width: '100%' }} disabled={isSubmitting}>
          {isSubmitting ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </AuthLayout>
  );
}
